<template>
  <div>
    <!-- search -->
    <search-nav @handleSearch="onClickSearch">
      <div class="search-layout w-full grid col-gap-4 flex-wrap xl:flex">
        <component
          v-for="(component, index) in stdSearchComponents"
          v-if="showSearchNavItem(component['options'])"
          :key="`search-${index}`"
          :is="component['component']"
          :label="component['label']"
          :query="component['query']"
          :options="component['options']"
          :onClickSearch="onClickSearch"
          v-model="nav.value[component['value']]"
        />
      </div>
    </search-nav>

    <div class="flex items-center justify-between mb-4">
      <vs-select v-model="perPage">
        <vs-select-item text="5개씩 보기" :value="5" />
        <vs-select-item text="10개씩 보기" :value="10" />
        <vs-select-item text="20개씩 보기" :value="20" />
        <vs-select-item text="50개씩 보기" :value="50" />
      </vs-select>
      <vs-button color="secondary" type="border" @click="onClickDownload">
        Excel 다운로드
      </vs-button>
    </div>

    <!-- 로딩 -->
    <div v-if="loading || violationLoading" class="mb-4">
      <vs-progress indeterminate color="primary" />
    </div>

    <!-- Data -->
    <div class="table-wrap" ref="tableWrap">
      <table>
        <!-- 토글 열을 제외한 모든 열은 동일한 너비 (table-layout: fixed + width 미지정) -->
        <colgroup>
          <col style="width: 44px" />
          <col />
          <col />
          <col />
          <col />
          <col />
          <col />
          <col />
        </colgroup>
        <thead>
          <tr>
            <th class="col-toggle"></th>
            <th>대상년도</th>
            <th class="sortable" @click="toggleSort('사업장명')">
              사업장명
              <span class="sort-icon">{{ sortIcon('사업장명') }}</span>
            </th>
            <th class="sortable" @click="toggleSort('사외수급업체명')">
              사외 수급업체명
              <span class="sort-icon">{{ sortIcon('사외수급업체명') }}</span>
            </th>
            <th class="sortable" @click="toggleSort('사업자등록번호')">
              사업자등록번호
              <span class="sort-icon">{{ sortIcon('사업자등록번호') }}</span>
            </th>
            <th class="sortable" @click="toggleSort('평균점수')">
              평균점수
              <span class="sort-icon">{{ sortIcon('평균점수') }}</span>
            </th>
            <th class="sortable" @click="toggleSort('감점')">
              <span class="th-multiline">
                <span>감점<br />(안전위반통보)</span>
                <span class="sort-icon">{{ sortIcon('감점') }}</span>
              </span>
            </th>
            <th class="sortable" @click="toggleSort('최종점수')">
              최종점수
              <span class="sort-icon">{{ sortIcon('최종점수') }}</span>
            </th>
          </tr>
        </thead>
        <tbody v-for="group in paginatedGroupedList" :key="group.key">
          <tr class="group-row" :class="{ 'row-active': isDetailGroup(group) }" @click="toggleGroup(group.key)">
            <td class="col-toggle">
              <span class="toggle-icon" :class="{ expanded: !!expandedGroups[group.key] }">&#9654;</span>
            </td>
            <td>{{ group.대상년도 }}</td>
            <td>{{ group.사업장명 }}</td>
            <td>{{ group.사외수급업체명 }}</td>
            <td>{{ group.사업자등록번호 || '-' }}</td>
            <td>{{ formatScore(group.평균점수) }}</td>
            <td class="deduction" :class="{ clickable: group.violations.length > 0 }" :title="group.violations.length > 0 ? '클릭 시 안전위반통보 상세 내역' : ''" @click="onClickDeduction(group, $event)">
              {{ group.감점 }}
            </td>
            <td>{{ formatScore(group.최종점수) }}</td>
          </tr>
          <tr v-if="expandedGroups[group.key]" class="detail-row">
            <td colspan="8">
              <table class="inner-table">
                <colgroup>
                  <col />
                  <col style="width: 130px" />
                  <col style="width: 200px" />
                  <col style="width: 200px" />
                  <col style="width: 100px" />
                </colgroup>
                <thead>
                  <tr>
                    <th class="text-right">공사명</th>
                    <th>구분</th>
                    <th>작업기간</th>
                    <th>총점</th>
                    <th>평균점수</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(child, idx) in group.items" :key="idx" :class="child.구분 === '최종평가 완료' ? 'row-final' : 'row-doc'">
                    <td class="text-right">{{ child.공사명 }}</td>
                    <td>{{ child.구분 }}</td>
                    <td>{{ child.작업기간 ? (child.작업기간.sDate || '') + ' ~ ' + (child.작업기간.eDate || '') : '-' }}</td>
                    <td>{{ child.총점 }}</td>
                    <td v-if="child.isFirstInGroup" :rowspan="child.groupRowspan">{{ child.groupAverage !== null && child.groupAverage !== undefined ? child.groupAverage.toFixed(1) : '-' }}</td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
        <tbody v-if="!loading && !violationLoading && paginatedGroupedList.length === 0">
          <tr>
            <td colspan="8">데이터가 없습니다.</td>
          </tr>
        </tbody>
      </table>

      <!-- 안전위반통보 누계 상세 팝업 -->
      <div v-if="detail.show" ref="detailPopup" class="detail-popup" :style="detail.style">
        <div class="detail-popup-header">
          <div>
            안전위반통보 누계
            <span class="detail-popup-summary">{{ detail.group.violations.length }}회 ({{ detail.summaryText }}) · 감점 {{ detail.group.감점 }}점</span>
          </div>
          <feather-icon icon="XIcon" svgClasses="h-5 w-5" class="detail-close" title="닫기" @click="closeDetail" />
        </div>
        <div class="detail-popup-body">
          <div v-if="detail.loading" class="detail-loading">불러오는 중...</div>
          <table v-else class="popup-table">
            <colgroup>
              <col />
              <col />
              <col />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th v-for="column in detailColumns" :key="column.field" class="sortable" @click="toggleDetailSort(column.field)">
                  {{ column.label }}
                  <span class="sort-icon">{{ detailSortIcon(column.field) }}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, itemIndex) in sortedDetailViolations" :key="`violation-${itemIndex}`" :class="gradeRowClass(item.등급)">
                <td>{{ item.발생일자 || '-' }}</td>
                <td>
                  <span class="grade-badge" :style="{ background: 등급색상[item.등급] || '#999' }">{{ item.등급 }}</span>
                </td>
                <td>{{ item.유형 || '-' }}</td>
                <td class="text-left">{{ item.세부내용 || '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="vs-con-table">
      <div class="con-pagination-table vs-table--pagination">
        <k-pagination :total="lastPage" v-model="page"></k-pagination>
      </div>
    </div>

    <popup-excel-download v-model="download" :list="excelList" title="사외 수급업체 평균점수 현황" />
  </div>
</template>

<script>
import SearchNav from "@/components/nav/SearchNav.vue";
import SearchSelect from "@/views/list/components/SearchSelect.vue";
import SearchColumnKeyword from "@/views/list/components/SearchColumnKeyword.vue";
import FieldSelector from "@/components/selector/FieldSelector.vue";
import searchComponent from "@/views/list/components/include";
import KPagination from "@/components/kPagination/kPagination.vue";
import PopupExcelDownload from "@/popup/PopupExcelDownload.vue";
import { 등급점수, 등급색상 } from "../1229/violationData";

// 안전위반통보서 게시판 (결재완료 건의 등급별 감점을 업체별로 합산한다)
const VIOLATION_BOARD_ID = 1229;

// 1229 조회를 897 업체로 좁힐 때 한 요청에 담는 IN 값 개수.
// IN 값은 GET 쿼리스트링(conditions[i][value][k])으로 직렬화되어 값 하나당 40~50바이트를 쓰므로
// Apache 기본 LimitRequestLine(8KB)에 걸리지 않도록 80개로 끊는다.
const VIOLATION_CHUNK_VALUES = 80;
// 동시에 띄우는 1229 요청 수 상한 (서버 커넥션 폭주 방지)
const VIOLATION_CONCURRENCY = 4;

export default {
  components: {
    KPagination,
    FieldSelector,
    SearchColumnKeyword,
    SearchNav,
    SearchSelect,
    PopupExcelDownload,
  },
  props: {
    isPrint: Boolean,
    board: Object,
    header: Object,
    value: Object,
    mode: {
      type: String,
      default: "list",
    },
  },
  computed: {
    projectId() {
      return this.$route.params.proj_id;
    },
    boardId() {
      return 897;
    },
    stdSearchComponents() {
      const stdSearchComponents = [
        {
          component: "search-year",
          label: "대상년도",
          value: "year",
          query: { y: "year" },
          options: {
            default: new Date().getFullYear(),
          },
        },
        {
          component: "search-select",
          label: "사업장명",
          value: "사업장명",
          query: { f: "사업장명" },
          options: {
            default: "",
            items: this.사업장명Items,
          },
        },
        {
          component: "search-start-end",
          label: "작업기간",
          value: "date",
          query: { ss: "startDate", ee: "endDate" },
          options: {
            default: { sd: "", ed: "" },
          },
        },
        {
          component: "search-keyword",
          label: "수급업체명",
          value: "수급업체명",
          query: { k: "수급업체명" },
          options: {
            default: "",
          },
        },
        {
          component: "search-select",
          label: "구분",
          value: "구분",
          query: { t: "구분" },
          options: {
            items: [
              { text: "전체", value: "" },
              { text: "서류평가 완료", value: "서류평가완료" },
              { text: "최종평가 완료", value: "최종평가완료" },
            ],
            default: "",
          },
        },
      ];

      for (const component of stdSearchComponents) {
        component.component = searchComponent[component["component"]];
      }

      return stdSearchComponents;
    },

    // 사업장명 선택 드롭다운 아이템 목록
    사업장명Items() {
      const seen = new Set();
      const items = [];
      for (const item of this.allProcessedList) {
        if (item.사업장명 && !seen.has(item.사업장명)) {
          seen.add(item.사업장명);
          items.push({ text: item.사업장명, value: item.사업장명 });
        }
      }
      items.sort((a, b) => {
        return a.value ? a.text.localeCompare(b.text) : -1;
      });
      items.unshift({ text: "전체", value: "" });
      return items;
    },

    // 전체 원시 데이터 → 처리된 항목 목록
    allProcessedList() {
      return this.allRawData.map((data) => {
        const content = data.postContents;
        let isFinish = false;
        if (content.body.evaluationList && content.body.evaluationList[1]) {
          const childs = content.body.evaluationList[1].childs;
          isFinish = childs.some((child) => Number(child.value) > 0);
        }
        const 업체 = content.body.업체명 || {};
        return {
          id: `${data.id}`,
          대상년도: content.header.year,
          구분: isFinish ? "최종평가 완료" : "서류평가 완료",
          사업장명: content.body.stdInfo.field_name,
          사외수급업체명: 업체.com_name || "",
          사업자등록번호: 업체.com_number || "",
          공사명: content.body.구분.공사명,
          작업기간: (content.body.구분 && content.body.구분.작업기간) ? content.body.구분.작업기간 : null,
          총점: content.body.userScore,
          업체id: 업체.id,
        };
      });
    },

    // 연도 내 897 에 등장한 전체 업체 키 (1229 조회 범위를 좁히는 데 쓴다)
    allCompanyKeys() {
      return this.collectCompanyKeys(this.allRawData);
    },

    // 안전위반통보서(결재완료) 원시 데이터 → 감점 집계용 통보 건 목록
    // 상세 내역(발생일자/유형/세부내용)은 감점 팝업을 열 때 해당 업체 건만 따로 조회한다.
    violationItems() {
      return this.violationRawData.map((data) => {
        const body = this.readViolationBody(data);
        const 업체 = body.업체 || {};
        return {
          업체id: 업체.id || 0,
          사업자등록번호: 업체.사업자등록번호 || "",
          등급: body.등급 || "",
          점수: 등급점수[body.등급] || 0,
        };
      });
    },

    // 업체id / 사업자등록번호 → 통보 건 목록 맵 (897 업체id ↔ 1229 업체.id 를 우선 조인, 사업자등록번호는 보조)
    violationMap() {
      const byId = {};
      const byNumber = {};
      for (const item of this.violationItems) {
        if (item.업체id) {
          if (!byId[item.업체id]) byId[item.업체id] = [];
          byId[item.업체id].push(item);
        }
        if (item.사업자등록번호) {
          if (!byNumber[item.사업자등록번호]) byNumber[item.사업자등록번호] = [];
          byNumber[item.사업자등록번호].push(item);
        }
      }
      return { byId, byNumber };
    },

    // 해당 년도 전체 데이터 기준 업체별 평균점수 맵 (최종평가 완료만 집계)
    averageScoreMap() {
      const map = {};
      for (const item of this.allProcessedList) {
        if (item.구분 !== '최종평가 완료') continue;
        const companyId = item.업체id;
        const score = Number(item.총점);
        if (map[companyId]) {
          map[companyId].sum += score;
          map[companyId].count += 1;
          map[companyId].average = map[companyId].sum / map[companyId].count;
        } else {
          map[companyId] = { sum: score, count: 1, average: score };
        }
      }
      return map;
    },

    // 클라이언트 사이드 필터링 (대상년도 제외)
    filteredList() {
      const search = this.nav.value;
      let list = this.allProcessedList;

      if (search.date) {
        if (search.date.startDate) {
          list = list.filter(
            (item) => item.작업기간 && item.작업기간.eDate >= search.date.startDate
          );
        }
        if (search.date.endDate) {
          const nextDay = new Date(search.date.endDate).addDays(1).format("yyyy-MM-dd");
          list = list.filter(
            (item) => item.작업기간 && item.작업기간.sDate < nextDay
          );
        }
      }

      if (search.사업장명) {
        list = list.filter((item) => item.사업장명 === search.사업장명);
      }

      if (search.수급업체명) {
        const kw = search.수급업체명.toLowerCase();
        list = list.filter((item) => item.사외수급업체명.toLowerCase().includes(kw)
        );
      }

      if (search.구분 === "서류평가완료") {
        list = list.filter((item) => item.구분 === "서류평가 완료");
      } else if (search.구분 === "최종평가완료") {
        list = list.filter((item) => item.구분 === "최종평가 완료");
      }

      return list;
    },

    // 업체별 그룹핑 (구분별 정렬 및 rowspan 정보 포함) + 안전위반통보 감점 합산
    groupedList() {
      const groups = {};
      for (const item of this.filteredList) {
        const key = item.업체id;
        if (!groups[key]) {
          groups[key] = {
            key,
            업체id: item.업체id,
            대상년도: item.대상년도,
            사업장명: item.사업장명,
            사외수급업체명: item.사외수급업체명,
            사업자등록번호: item.사업자등록번호,
            평균점수: null,
            감점: 0,
            최종점수: null,
            violations: [],
            violationMatch: null,
            gradeCounts: { 경미: 0, 주의: 0, 심각: 0 },
            items: [],
          };
        }
        // 이전 글에 사업자등록번호가 비어 있어도 같은 업체의 다른 글에서 채운다
        if (!groups[key].사업자등록번호 && item.사업자등록번호) {
          groups[key].사업자등록번호 = item.사업자등록번호;
        }
        groups[key].items.push({
          구분: item.구분,
          공사명: item.공사명,
          작업기간: item.작업기간,
          총점: item.총점,
        });
      }

      for (const group of Object.values(groups)) {
        // 구분별 정렬 (최종평가 완료 → 서류평가 완료 순)
        group.items.sort((a, b) => {
          if (a.구분 === b.구분) return 0;
          return a.구분 === '최종평가 완료' ? -1 : 1;
        });

        // 구분별 합계/건수/평균 계산
        const 구분Map = {};
        for (const item of group.items) {
          if (!구분Map[item.구분]) 구분Map[item.구분] = { sum: 0, count: 0 };
          구분Map[item.구분].sum += Number(item.총점);
          구분Map[item.구분].count += 1;
        }
        for (const g of Object.values(구분Map)) {
          g.average = g.sum / g.count;
        }

        // 외부 행: 최종평가 완료 평균만 표시
        const finalData = 구분Map['최종평가 완료'];
        group.평균점수 = finalData ? finalData.average : null;

        // 각 item에 구분별 rowspan 정보 부여
        let currentGroup = null;
        for (const item of group.items) {
          if (item.구분 !== currentGroup) {
            currentGroup = item.구분;
            item.isFirstInGroup = true;
            item.groupRowspan = 구분Map[item.구분].count;
            item.groupAverage = 구분Map[item.구분].average;
          } else {
            item.isFirstInGroup = false;
          }
        }

        // 안전위반통보 감점: 업체id 로 조인, 없으면 사업자등록번호로 보조 조인
        // 상세 조회도 같은 키로 해야 결과가 어긋나지 않으므로 어느 키로 맞췄는지 group 에 남긴다.
        let violations = this.violationMap.byId[group.업체id] || [];
        let violationMatch = violations.length > 0 ? { key: "body.업체.id", value: `${group.업체id}` } : null;
        if (violations.length === 0 && group.사업자등록번호) {
          violations = this.violationMap.byNumber[group.사업자등록번호] || [];
          if (violations.length > 0) violationMatch = { key: "body.업체.사업자등록번호", value: group.사업자등록번호 };
        }
        group.violations = violations;
        group.violationMatch = violationMatch;
        if (!group.사업자등록번호 && group.violations.length > 0) {
          group.사업자등록번호 = group.violations[0].사업자등록번호 || "";
        }
        for (const violation of group.violations) {
          group.감점 += violation.점수;
          if (group.gradeCounts[violation.등급] !== undefined) group.gradeCounts[violation.등급] += 1;
        }
        group.최종점수 = group.평균점수 !== null ? group.평균점수 + group.감점 : null;
      }

      return Object.values(groups);
    },

    // 감점 팝업 목록 정렬 (기본: 발생일자 내림차순, 등급은 경미<주의<심각 순)
    sortedDetailViolations() {
      if (!this.detail.group) return [];
      const list = [...this.detail.items];
      const { field, dir } = this.detail.sort;
      if (!field) return list;
      const sign = dir === "asc" ? 1 : -1;
      const gradeRank = (grade) => {
        const index = this.gradeOrder.indexOf(grade);
        return index < 0 ? this.gradeOrder.length : index;
      };
      return list.sort((a, b) => {
        if (field === "등급") return (gradeRank(a.등급) - gradeRank(b.등급)) * sign;
        return `${a[field] || ""}`.localeCompare(`${b[field] || ""}`) * sign;
      });
    },

    // 업체id → 그룹 (엑셀 다운로드에서 감점/최종점수 조회용)
    groupMap() {
      const map = {};
      for (const group of this.groupedList) {
        map[group.업체id] = group;
      }
      return map;
    },

    // 정렬 적용
    processedGroupedList() {
      let list = this.groupedList;

      if (this.sort.field) {
        const { field, dir } = this.sort;
        list = [...list].sort((a, b) => {
          const va = a[field];
          const vb = b[field];
          const aNil = va === null || va === undefined;
          const bNil = vb === null || vb === undefined;
          if (aNil && bNil) return 0;
          if (aNil) return 1;
          if (bNil) return -1;
          if (typeof va === 'string') return dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
          return dir === 'asc' ? va - vb : vb - va;
        });
      }

      return list;
    },

    // 전체 페이지 수 (업체 기준)
    lastPage() {
      return Math.max(1, Math.ceil(this.processedGroupedList.length / this.perPage));
    },

    // 현재 페이지에 표시할 그룹 목록
    paginatedGroupedList() {
      const start = (this.page - 1) * this.perPage;
      return this.processedGroupedList.slice(start, start + this.perPage);
    },

    // 엑셀 다운로드용 목록 (전체 필터링 결과, 페이지 구분 없음)
    excelList() {
      return this.filteredList.map((item) => {
        const avgData = this.averageScoreMap[item.업체id];
        const 평균점수 = avgData ? Math.round(avgData.average * 10) / 10 : null;
        const group = this.groupMap[item.업체id];
        const 감점 = group ? group.감점 : 0;
        const 최종점수 = 평균점수 !== null ? Math.round((평균점수 + 감점) * 10) / 10 : null;
        const 작업기간 = item.작업기간 ? `${item.작업기간.sDate || ""} ~ ${item.작업기간.eDate || ""}` : "-";
        return {
          "대상년도": item.대상년도,
          "구분": item.구분,
          "사업장명": item.사업장명,
          "사외수급업체명": item.사외수급업체명,
          "사업자등록번호": (group && group.사업자등록번호) || item.사업자등록번호 || "-",
          "공사명": item.공사명,
          작업기간,
          "총점": item.총점,
          평균점수,
          "감점(안전위반통보)": 감점,
          최종점수,
        };
      });
    },
  },
  data() {
    return {
      등급색상,
      gradeOrder: ["경미", "주의", "심각"],
      nav: {
        value: {
          year: new Date().getFullYear(),
        },
      },

      allRawData: [],
      // 1229 는 화면에 보이는 업체 단위로 나눠 받아 여기에 누적한다
      violationRawData: [],
      // 조회를 마친 업체 키(0건이어도 조회 완료로 본다) → 같은 업체를 다시 요청하지 않는다
      loadedCompanyKeys: {},
      // 연도 내 전체 업체를 다 받았는지 (감점 정렬 / 엑셀 다운로드에 필요)
      violationAllLoaded: false,
      violationLoading: false,
      loading: false,

      page: 1,
      perPage: 10,

      sort: { field: null, dir: 'desc' },

      download: false,
      expandedGroups: {},

      detailColumns: [
        { field: "발생일자", label: "발생일시" },
        { field: "등급", label: "등급" },
        { field: "유형", label: "위반유형" },
        { field: "세부내용", label: "세부내용" },
      ],
      // 상세 조회 응답이 늦게 도착했을 때 버릴지 판단하는 토큰
      detailRequestToken: 0,
      detail: {
        show: false,
        group: null,
        style: {},
        summaryText: "",
        sort: { field: "발생일자", dir: "desc" },
        loading: false,
        items: [],
      },
    };
  },
  mounted() {
    // 팝업 외부 클릭 시 닫기
    document.addEventListener("click", this.onDocumentClick);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onDocumentClick);
  },
  watch: {
    "nav.value.year": {
      immediate: true,
      handler() {
        this.loadAllFromApi();
      },
    },
    // 표시 중인 업체가 바뀌면(페이지 이동 / 검색 / 정렬) 그 업체의 감점을 아직 안 받았을 때만 받아온다
    paginatedGroupedList() {
      this.syncViolationsForPage();
    },
    perPage() {
      this.page = 1;
      this.closeDetail();
    },
    page() {
      this.closeDetail();
    },
    lastPage(val) {
      if (this.page > val) this.page = val;
    },
  },
  methods: {
    showSearchNavItem(options) {
      if (options && options.visibleKeyValue) {
        for (const keyValue of options.visibleKeyValue) {
          const key = Object.keys(keyValue)[0];
          const value = keyValue[key];
          const navValue = this.findJsonDataWithDottedPath(this.nav.value, key);
          if (navValue !== value) {
            if (Array.isArray(navValue) && navValue.toString() === value) {
              return true;
            }
          } else {
            return true;
          }
        }
        return false;
      }
      return true;
    },

    onClickSearch() {
      this.page = 1;
      this.closeDetail();
    },

    async onClickDownload() {
      // 엑셀은 현재 페이지가 아니라 검색 결과 전체를 담으므로 감점도 전체가 필요하다
      this.loading = true;
      await this.ensureAllViolations();
      this.loading = false;
      this.download = true;
    },

    // 점수 표시: 정수는 그대로, 소수는 소수점 1자리
    formatScore(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) return "-";
      const num = Number(value);
      return Number.isInteger(num) ? `${num}` : num.toFixed(1);
    },

    sortIcon(field) {
      if (this.sort.field !== field) return "⇅";
      return this.sort.dir === "asc" ? "▲" : "▼";
    },

    toggleGroup(key) {
      this.closeDetail();
      this.$set(this.expandedGroups, key, !this.expandedGroups[key]);
    },

    async toggleSort(field) {
      // 감점 / 최종점수는 전체 업체의 감점이 있어야 순서가 맞는다
      if (field === "감점" || field === "최종점수") await this.ensureAllViolations();

      if (this.sort.field === field) {
        if (this.sort.dir === 'desc') {
          this.sort.dir = 'asc';
        } else {
          // asc → 해제
          this.sort.field = null;
          this.sort.dir = 'desc';
        }
      } else {
        this.sort.field = field;
        this.sort.dir = 'desc';
      }
      this.page = 1;
      this.closeDetail();
    },

    // 팝업 컬럼 정렬: 내림차순 → 오름차순 → 기본(발생일자 내림차순)
    toggleDetailSort(field) {
      const sort = this.detail.sort;
      if (sort.field === field) {
        if (sort.dir === "desc") {
          sort.dir = "asc";
        } else {
          sort.field = "발생일자";
          sort.dir = "desc";
        }
      } else {
        sort.field = field;
        sort.dir = "desc";
      }
    },

    detailSortIcon(field) {
      if (this.detail.sort.field !== field) return "⇅";
      return this.detail.sort.dir === "asc" ? "▲" : "▼";
    },

    gradeRowClass(grade) {
      if (grade === "경미") return "grade-minor";
      if (grade === "주의") return "grade-warning";
      if (grade === "심각") return "grade-severe";
      return "";
    },

    isDetailGroup(group) {
      return this.detail.show && this.detail.group && this.detail.group.key === group.key;
    },

    onDocumentClick(event) {
      if (!this.detail.show) return;
      const popup = this.$refs.detailPopup;
      if (popup && popup.contains(event.target)) return;
      // 감점 셀 클릭은 onClickDeduction 이 토글로 처리하므로 제외
      if (event.target.closest && event.target.closest("td.deduction.clickable")) return;
      this.closeDetail();
    },

    // 감점 셀 클릭: 통보 내역이 있을 때만 상세 팝업 (없으면 행 펼침/접힘으로 그대로 전달)
    onClickDeduction(group, event) {
      if (!group.violations.length) return;
      event.stopPropagation();

      // 같은 행을 다시 클릭하면 닫기
      if (this.isDetailGroup(group)) {
        this.closeDetail();
        return;
      }

      this.detail.summaryText = this.gradeOrder
        .filter((grade) => group.gradeCounts[grade] > 0)
        .map((grade) => `${grade} ${group.gradeCounts[grade]}건`)
        .join(" · ");

      // 클릭한 셀 바로 아래에 팝업 표시, 테이블 영역을 벗어나지 않게 클램프
      let top = 40;
      let left = 8;
      try {
        const wrapRect = this.$refs.tableWrap.getBoundingClientRect();
        const cellRect = event.currentTarget.getBoundingClientRect();
        const popupWidth = Math.min(640, wrapRect.width - 16);
        top = cellRect.bottom - wrapRect.top + 6;
        left = cellRect.left - wrapRect.left + (cellRect.width / 2) - (popupWidth / 2);
        left = Math.max(8, Math.min(left, wrapRect.width - popupWidth - 8));
      } catch (e) {
        /* ignore */
      }
      this.detail.style = { top: `${top}px`, left: `${left}px` };
      this.detail.sort = { field: "발생일자", dir: "desc" };
      this.detail.group = group;
      this.detail.show = true;
      this.loadViolationDetail(group);
    },

    closeDetail() {
      this.detail.show = false;
      this.detail.group = null;
      this.detail.loading = false;
      this.detail.items = [];
      // 진행 중인 상세 조회 응답을 무효화한다
      this.detailRequestToken += 1;
    },

    // 대상년도 변경 시 평가(897)만 전량 받는다.
    // 안전위반통보(1229)는 화면에 보이는 업체 단위로 syncViolationsForPage 가 나눠 받는다.
    async loadAllFromApi() {
      this.loading = true;
      this.closeDetail();
      const year = this.nav.value.year;

      const allRawData = await this.loadEvaluationAll(year);

      // 연도가 바뀌면 누적해 둔 감점은 모두 버린다
      this.violationRawData = [];
      this.loadedCompanyKeys = {};
      this.violationAllLoaded = false;
      this.allRawData = allRawData;
      this.page = 1;
      this.loading = false;

      await this.$nextTick();
      await this.syncViolationsForPage();
    },

    // page 1 로 last_page 를 확인한 뒤 남은 페이지를 받아 합친다
    async loadAllPages(action, baseOptions) {
      const firstResponse = await this.$store.dispatch(action, { ...baseOptions, page: 1 });
      let allData = [...firstResponse.data];
      const lastPage = Number(firstResponse.last_page) || 1;
      if (lastPage > 1) {
        const pagePromises = [];
        for (let p = 2; p <= lastPage; p++) {
          pagePromises.push(this.$store.dispatch(action, { ...baseOptions, page: p }));
        }
        const remainingResponses = await Promise.all(pagePromises);
        for (const response of remainingResponses) {
          allData = allData.concat(response.data);
        }
      }
      return allData;
    },

    // 수급업체 안전보건수준평가(897) 전체 로드 (200개 단위 청크)
    async loadEvaluationAll(year) {
      return this.loadAllPages("board/LOAD_POST_LIST", {
        projectId: this.projectId,
        boardId: this.boardId,
        perPage: 200,
        conditions: [{ key: "header.year", operator: "=", value: `${year}` }],
        withMeta: true,
        withoutRelation: true,
        onlyMeta: true,
        metaKeys: [
          "body.stdInfo.field_name",
          "header.year",
          "body.업체명.com_name",
          "body.업체명.com_number",
          "body.업체명.id",
          "body.구분.공사명",
          "body.구분.작업기간%",
          "body.userScore",
          "body.evaluationList[1].childs%",
        ],
        orderTarget: "$body.업체명.com_name",
        orderDirection: "desc",
      });
    },

    // 897 목록에 등장한 업체 키 수집.
    // 업체id 와 사업자등록번호를 함께 모으는 이유는 조인이 둘 중 하나로 이뤄지기 때문이다(violationMap 참고).
    collectCompanyKeys(rawData) {
      const companies = [];
      const seen = new Set();
      for (const data of rawData) {
        const content = data.postContents || {};
        const 업체 = (content.body && content.body.업체명) || {};
        const id = 업체.id ? `${업체.id}` : "";
        const number = 업체.com_number ? `${업체.com_number}` : "";
        if (!id && !number) continue;
        const key = this.companyKey({ id, number });
        if (seen.has(key)) continue;
        seen.add(key);
        companies.push({ id, number });
      }
      return companies;
    },

    companyKey(company) {
      return `${company.id || ""}|${company.number || ""}`;
    },

    // 현재 페이지에 보이는 업체 중 아직 감점을 안 받은 곳만 조회한다.
    // 조회 결과로 groupedList 가 다시 계산되면 이 핸들러가 한 번 더 돌지만,
    // 그때는 받을 것이 없어 요청 없이 끝난다.
    async syncViolationsForPage() {
      if (this.violationAllLoaded || this.violationLoading) return;

      // group.사업자등록번호 는 위반 내역에서 역으로 채워질 수 있어 키가 변한다.
      // 897 에서 모은 키(allCompanyKeys)는 변하지 않으므로 그쪽을 기준으로 고른다.
      const visibleIds = new Set(this.paginatedGroupedList.map((group) => (group.업체id ? `${group.업체id}` : "")));
      const pending = this.allCompanyKeys.filter((company) => visibleIds.has(company.id) && !this.loadedCompanyKeys[this.companyKey(company)]);

      if (pending.length === 0) return;
      await this.loadViolationsFor(pending);
    },

    // 감점 정렬 / 엑셀 다운로드처럼 전체 업체의 감점이 필요한 경우에 쓴다
    async ensureAllViolations() {
      if (this.violationAllLoaded) return;

      const pending = this.allCompanyKeys.filter((company) => !this.loadedCompanyKeys[this.companyKey(company)]);
      if (pending.length > 0) await this.loadViolationsFor(pending);
      // 조회에 실패해 남은 업체가 있으면 다음 기회에 다시 시도한다
      this.violationAllLoaded = this.allCompanyKeys.every((company) => this.loadedCompanyKeys[this.companyKey(company)]);
    },

    // 넘겨받은 업체들의 안전위반통보(1229) 결재완료 건을 받아 누적한다
    async loadViolationsFor(companies) {
      if (!companies || companies.length === 0) return;

      this.violationLoading = true;
      try {
        const rows = await this.loadViolationAll(this.nav.value.year, companies);
        // 0건이어도 "조회했음"으로 남겨 같은 업체를 다시 요청하지 않는다
        for (const company of companies) {
          this.$set(this.loadedCompanyKeys, this.companyKey(company), true);
        }
        this.violationRawData = this.mergeViolationRows(this.violationRawData, rows);
      } catch (e) {
        // 게시판 미생성 / 조회 실패 시 감점 0 으로 둔다.
        // 조회 완료 표시를 남기지 않으므로 다음 기회에 다시 시도한다.
      } finally {
        this.violationLoading = false;
      }

      // 조회 중 페이지가 바뀌었을 수 있으므로 한 번 더 확인한다
      await this.$nextTick();
      await this.syncViolationsForPage();
    },

    // 업체id 조회분과 사업자등록번호 조회분이 겹칠 수 있어 게시글 기준으로 중복을 제거한다
    mergeViolationRows(...lists) {
      const merged = [];
      const seen = new Set();
      for (const list of lists) {
        for (const data of list || []) {
          const postId = (data.post && data.post.id) || data.id;
          if (postId !== undefined && postId !== null) {
            if (seen.has(postId)) continue;
            seen.add(postId);
          }
          merged.push(data);
        }
      }
      return merged;
    },

    // 업체 키를 IN 조건 목록으로 만든다.
    // 업체id 와 사업자등록번호를 OR 로 묶지 않고 요청을 나누는 이유:
    // 서로 다른 meta key 를 OR 로 묶으면 인덱스를 못 타 조회가 급격히 느려진다.
    buildViolationConditions(companies) {
      const ids = [...new Set(companies.map((company) => company.id).filter(Boolean))];
      const numbers = [...new Set(companies.map((company) => company.number).filter(Boolean))];

      const conditions = [];
      for (let i = 0; i < ids.length; i += VIOLATION_CHUNK_VALUES) {
        conditions.push({ key: "body.업체.id", operator: "IN", value: ids.slice(i, i + VIOLATION_CHUNK_VALUES) });
      }
      for (let i = 0; i < numbers.length; i += VIOLATION_CHUNK_VALUES) {
        conditions.push({ key: "body.업체.사업자등록번호", operator: "IN", value: numbers.slice(i, i + VIOLATION_CHUNK_VALUES) });
      }
      return conditions;
    },

    // 안전위반통보서(1229) 결재완료 건 조회 (연도 귀속은 발급일자 기준 — 1227 집계와 동일)
    // 감점 집계에 필요한 meta 만 받는다. 상세 내역은 감점 팝업에서 업체 단위로 따로 조회한다.
    async loadViolationAll(year, companies) {
      const companyConditions = this.buildViolationConditions(companies || []);
      if (companyConditions.length === 0) return [];

      const baseOptions = {
        projectId: this.projectId,
        boardSlug: `board-${VIOLATION_BOARD_ID}`,
        perPage: 200,
        statusIds: [4], // 승인(결재완료)만
        withMeta: true,
        // 작성자 / 프로젝트 / 게시판 / 사업장 관계는 감점 집계에 쓰지 않으므로 제외한다
        withoutRelation: true,
        onlyMeta: true,
        metaKeys: ["body.업체.id", "body.업체.사업자등록번호", "body.등급"],
      };
      const yearCondition = { key: "body.발급일자", operator: "LIKE", value: `${year}-%` };

      // 실패는 호출자(loadViolationsFor)가 처리한다. 여기서 삼키면 다시 시도할 수 없다.
      const results = await this.runWithConcurrency(
        companyConditions.map((condition) => () => this.loadAllPages("approval/LOAD_POST_ALL", { ...baseOptions, conditions: [yearCondition, condition] })),
        VIOLATION_CONCURRENCY
      );
      return this.mergeViolationRows(...results);
    },

    // 동시 실행 수를 제한해 순차 배치로 돌린다
    async runWithConcurrency(tasks, limit) {
      const results = [];
      for (let i = 0; i < tasks.length; i += limit) {
        const batch = tasks.slice(i, i + limit).map((task) => task());
        results.push(...(await Promise.all(batch)));
      }
      return results;
    },

    // 감점 팝업: 클릭한 업체의 통보 상세만 조회한다(목록 집계에는 등급만 필요하므로 미리 받지 않는다)
    async loadViolationDetail(group) {
      const token = ++this.detailRequestToken;
      this.detail.loading = true;
      this.detail.items = [];

      let items = [];
      try {
        items = await this.loadViolationDetailAll(this.nav.value.year, group);
      } catch (e) {
        items = [];
      }

      // 응답이 오는 사이 팝업을 닫았거나 다른 행을 클릭했으면 결과를 버린다
      if (token !== this.detailRequestToken) return;
      this.detail.items = items;
      this.detail.loading = false;
    },

    async loadViolationDetailAll(year, group) {
      const match = group.violationMatch;
      if (!match || !match.value) return [];

      const rawData = await this.loadAllPages("approval/LOAD_POST_ALL", {
        projectId: this.projectId,
        boardSlug: `board-${VIOLATION_BOARD_ID}`,
        perPage: 200,
        statusIds: [4], // 승인(결재완료)만
        conditions: [
          { key: "body.발급일자", operator: "LIKE", value: `${year}-%` },
          { key: match.key, operator: "=", value: match.value },
        ],
        withMeta: true,
        withoutRelation: true,
        onlyMeta: true,
        metaKeys: ["body.등급", "body.위반내역%", "body.세부설명", "body.발생일자"],
      });

      return rawData
        .map((data) => {
          const body = this.readViolationBody(data);
          return {
            발생일자: body.발생일자 || "",
            등급: body.등급 || "",
            유형: (body.위반내역 && body.위반내역.유형) || "",
            세부내용: body.세부설명 || "",
          };
        })
        .sort((a, b) => (a.발생일자 < b.발생일자 ? 1 : -1));
    },

    readViolationBody(data) {
      try {
        const content = data.postContents ? data.postContents : data.post_content ? JSON.parse(data.post_content) : {};
        return content.body || {};
      } catch (e) {
        return {};
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.table-wrap {
  position: relative;
}

table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;

  th,
  td {
    border: 1px solid #cdcdcd;
    padding: 16px 7px;
  }

  th {
    font-size: 14px;
    font-weight: 500;
    text-align: center;
    background: #f9f9f9;
  }

  td {
    font-size: 14px;
    font-weight: 400;
    text-align: center;
    background: white;
  }

  .col-toggle {
    width: 44px;
    padding: 0;
  }

  .sortable {
    cursor: pointer;
    user-select: none;
    white-space: nowrap;

    &:hover {
      background: #ececec;
    }
  }

  .sort-icon {
    display: inline-block;
    margin-left: 4px;
    font-size: 11px;
    color: #999;
    vertical-align: middle;
  }

  // 2줄 헤더(감점) : 텍스트와 정렬 아이콘을 가로로 나란히
  .th-multiline {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    line-height: 1.3;

    .sort-icon {
      margin-left: 0;
    }
  }

  // 그룹 사이 구분선: 각 tbody의 첫 번째 tr 상단에 두꺼운 선
  tbody + tbody .group-row td {
    border-top: 2px solid #b0b8c4;
  }

  .group-row {
    cursor: pointer;

    td {
      background: #f0f2f5;
      font-weight: 500;
    }

    // 왼쪽 accent 테두리: 첫 번째 td에 box-shadow로 구현
    td:first-child {
      box-shadow: inset 4px 0 0 rgba(var(--vs-primary), 1);
    }

    &:hover td {
      background: rgba(var(--vs-primary), 0.1);
    }

    // 감점 팝업이 열린 행 강조
    &.row-active td {
      background: rgba(var(--vs-primary), 0.12);
    }

    .deduction.clickable {
      color: #5b3cc4;
      font-weight: 700;
      text-decoration: underline dotted;
      text-underline-offset: 3px;

      &:hover {
        background: rgba(var(--vs-primary), 0.2);
      }
    }
  }

  .toggle-icon {
    display: inline-block;
    font-size: 11px;
    color: #666;
    transition: transform 0.2s ease;

    &.expanded {
      transform: rotate(90deg);
    }
  }

  .detail-row {
    > td {
      padding: 0;
      background: #f7f8fa;
      border-top: none;
      // 왼쪽 accent 테두리 연결
      box-shadow: inset 4px 0 0 rgba(var(--vs-primary), 0.35);
    }

    &:hover td {
      background: #f7f8fa;
    }
  }
}

.inner-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;

  th,
  td {
    border: 1px solid #e0e0e0;
    padding: 10px 12px;
    font-size: 14px;
  }

  th {
    background: #eef0f4;
    font-weight: 500;
    text-align: center;
  }

  td {
    background: #eee;
    text-align: center;
  }

  td.text-right,
  th.text-right {
    text-align: right;
  }

  .row-doc td {
    background: #f3f4f7;
    color: #555;
  }

  .row-final td {
    background: #eaf0fb;
    color: #2c4a8e;
  }

  tbody tr:hover td {
    background: rgba(var(--vs-primary), 0.08);
    color: inherit;
  }
}

/* 감점(안전위반통보) 상세 팝업 */
.detail-popup {
  position: absolute;
  /* vuesax 페이지네이션 버튼이 z-index 200 이라 그보다 위로 */
  z-index: 1000;
  width: 640px;
  max-width: calc(100% - 16px);
  background: #fff;
  border: 1px solid #d5d5d5;
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  overflow: hidden;
  text-align: left;

  .detail-popup-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    font-size: 14px;
    font-weight: 700;
    padding: 12px 14px;

    .detail-popup-summary {
      font-size: 12px;
      font-weight: 500;
      color: #777;
      margin-left: 6px;
    }

    .detail-close {
      color: #888;
      cursor: pointer;
      flex-shrink: 0;

      &:hover {
        color: #ea5455;
      }
    }
  }

  .detail-popup-body {
    padding: 0 14px 14px;
    max-height: 320px;
    overflow-y: auto;
  }

  .detail-loading {
    padding: 24px 0;
    text-align: center;
    font-size: 13px;
    color: #888;
  }

  .popup-table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;

    th,
    td {
      padding: 8px 7px;
      font-size: 13px;
      border: 1px solid #e0e0e0;
      text-align: center;
      word-break: keep-all;
      overflow-wrap: anywhere;
    }

    th {
      background: #f9f9f9;
      font-weight: 500;
      white-space: nowrap;
    }

    th.sortable {
      cursor: pointer;
      user-select: none;

      &:hover {
        background: #ececec;
      }

      .sort-icon {
        display: inline-block;
        margin-left: 4px;
        font-size: 11px;
        color: #999;
        vertical-align: middle;
      }
    }

    .text-left {
      text-align: left;
    }

    .grade-badge {
      display: inline-block;
      color: #fff;
      font-size: 12px;
      font-weight: 600;
      border-radius: 4px;
      padding: 2px 8px;
    }

    tr.grade-minor td {
      background: #eaf9fc;
    }
    tr.grade-warning td {
      background: #fdf3e6;
    }
    tr.grade-severe td {
      background: #fdecec;
    }
  }
}
</style>
